import React, { useState, useEffect } from "react";
import { Button, ControlLabel, Form, FormControl, FormGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    initiateRegistration,
    completeRegistration,
    fetchRegistration,
} from "./accountApi";
import { assert } from "../util/assert";
import { Link } from "react-router-dom";

import { PasswordData, CenterContainer, PasswordForm, validatePassword } from "./Common";
import "./Account.css";

type RegistrationProps = {
    withPassword: boolean,
    token: string
};

type UserData = {
    firstName?: string,
    lastName?: string,
    username?: string,
    passwordData?: PasswordData,
}

type RegistrationState =
    | { state: "invalidToken" }
    | {
        state: "transitionMessage",
        message: "validate",
        username: string,
        firstName: string,
        lastName: string,
    }
    | {
        state: "transitionMessage",
        message: "complete"
    }
    | {
        state: "formInput"
    } & UserData

const AfterCompleteRegistration = () => {
    return (
        <CenterContainer>
            <div className="rg-shadow-box rg-message-content">
                <h2>You're all ready to go</h2>
                <FontAwesomeIcon
                    icon={["fas", "check-circle"]}
                    size="3x"
                    color="lightgray"
                    className="rg-email-icon"
                />
                <p>
                    Return to the <Link to="/">homepage</Link> to log in!
                </p>
            </div>
        </CenterContainer>
    );
};

const AfterInitiateRegistration = ({ username, onSubmit }: { username?: string; onSubmit(): void }) => {
    assert(username, "Should not get to this page with username undefined");

    const [verificationResent, setVerificationResent] = useState(false);
    return (
        <CenterContainer>
            <div className="rg-shadow-box rg-message-content">
                <h2>Verify your email</h2>
                <FontAwesomeIcon
                    icon={["fas", "envelope"]}
                    size="3x"
                    color="lightgray"
                    className="rg-email-icon"
                />
                <p>
                    We sent a verification email to { username }. Click the link in your email to get started!
                </p>
                <p>
                    It may take up to 10 minutes for the email to arrive. Check your spam folder if you can't find the email or click the link below to re-send.
                </p>
                <Button
                    bsStyle="link"
                    className="rg-resend"
                    onClick={ () => { setVerificationResent(true); onSubmit() } }
                >
                    Resend verification email
                </Button>
                { verificationResent && <div>Verification email has been resent</div> }
            </div>
        </CenterContainer>
    );
};

export const Registration = ({ withPassword, token }: RegistrationProps) => {
    const [registrationState, setRegistrationState] = useState<RegistrationState>({ state: "formInput" });

    useEffect(() => {
        if (token) {
            fetchRegistration(token).then(userData => {
                setRegistrationState(prevState => ({
                    ...prevState,
                    ...userData
                }));
            }).catch(error => {
                if (error.statusCode === 400) {
                    setRegistrationState({
                        state: "invalidToken"
                    });
                }
            });
        };
    }, [token]);

    const allowSubmit = () => {
            if (registrationState.state !== "formInput") {
                return false;
            }
            const { username, firstName, lastName, passwordData } = registrationState;
            return firstName
                && lastName
                && username
                && (!withPassword || validatePassword(passwordData));
    }


    const submitInitiateRegistration = (userData: UserData) => {
        if (!userData.firstName || !userData.lastName || !userData.username) {
            // Submit disabled in this case, should not get here
            throw new Error("Missing user data");
        }
        const { firstName, lastName, username } = userData;
        initiateRegistration({ firstName, lastName, username }).then(() => {
            setRegistrationState({
                firstName,
                lastName,
                username,
                state: "transitionMessage",
                message: "validate",
            });
        });
    };

    const submitCompleteRegistration = (userData: UserData) => {
        if (!userData.firstName
            || !userData.lastName
            || !userData.username
            || !userData.passwordData
            || typeof token !== "string"
        ) {
            // Submit button disabled, should not get here
            throw new Error("Missing user data or malformed token");
        }
        const { firstName, lastName, username } = userData;
        const password = userData.passwordData.password

        completeRegistration({ firstName, lastName, username, password, token }).then(() => {
            setRegistrationState({
                state: "transitionMessage",
                message: "complete"
            });
        });
    };

    if (registrationState.state === "transitionMessage") {
        return registrationState.message === "complete"
            ? <AfterCompleteRegistration />
            : <AfterInitiateRegistration
                username={ registrationState.username }
                onSubmit={ () => submitInitiateRegistration(registrationState) }
            />
    } else if (registrationState.state === "invalidToken") {
        return (
            <div>
                <CenterContainer>
                    <div className="rg-shadow-box rg-message-content">
                        <h2>Invalid Link</h2>
                        <FontAwesomeIcon
                            icon={["fas", "times-circle"]}
                            size="3x"
                            color="lightgray"
                            className="rg-email-icon"
                        />
                        <p>
                            The link you have followed is invalid. The link sent in the verification email expires after one hour. Please visit the <Link to="/register" onClick={ () => setRegistrationState({ state: "formInput" }) }>registration page</Link> to re-submit your registration details.
                        </p>
                    </div>
                </CenterContainer>
            </div>
        );
    } else {
        return (
            <div>
                <div className="rg-open-text">
                    { withPassword
                        ? <>Almost ready to go. Create a password to get started using QBank.</>
                        : <>Start learning today. You're 2 minutes away from accessing a question bank.</>
                    }
                </div>
                <CenterContainer>
                    <div className="rg-shadow-box">
                        <Form
                            onSubmit={
                                e => { e.preventDefault(); withPassword
                                    ? submitCompleteRegistration(registrationState)
                                    : submitInitiateRegistration(registrationState)
                                }
                            }
                        >
                            <FormGroup>
                                <ControlLabel>First Name</ControlLabel>
                                <FormControl
                                    type="text"
                                    autoComplete="given-name"
                                    autoFocus
                                    label="First Name"
                                    defaultValue={registrationState.firstName || ""}
                                    onChange={e => setRegistrationState({
                                        ...registrationState,
                                        firstName: (e.target as any).value
                                    })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <ControlLabel>Last Name</ControlLabel>
                                <FormControl
                                    type="text"
                                    autoComplete="family-name"
                                    label="Last Name"
                                    defaultValue={registrationState.lastName || ""}
                                    onChange={e => setRegistrationState({
                                        ...registrationState,
                                        lastName: (e.target as any).value
                                    })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <ControlLabel>Email</ControlLabel>
                                <FormControl
                                    type="email"
                                    autoComplete="email"
                                    label="Email"
                                    defaultValue={registrationState.username || ""}
                                    disabled={withPassword}
                                    onChange={e => setRegistrationState({
                                        ...registrationState,
                                        username: (e.target as any).value
                                    })}
                                />
                            </FormGroup>
                            { withPassword && <PasswordForm
                                passwordData={registrationState.passwordData}
                                setPasswordData={ data => setRegistrationState({
                                    ...registrationState,
                                    passwordData: data
                                })} />
                            }
                            <div className="rg-register-buttons">
                                <Button className="rg-cancel-button" bsStyle="link">Cancel</Button>
                                <Button
                                    className="rg-register-button"
                                    bsStyle="link"
                                    disabled={!allowSubmit}
                                    type="submit"
                                >
                                    Register
                                </Button>
                            </div>
                        </Form>
                    </div>
                    <div className="rg-signin rg-shadow-box">
                        Already have a QBank account? <Link to="/">Sign In</Link>
                    </div>
                </CenterContainer>
            </div>
        );
    }
};
